import type { UseFetchStateProps } from "./useFetch";

import * as React from "react";
import { v4 as uuidv4 } from "uuid";

import { useFetch } from "./useFetch";
import { useSite } from "./useSite";

export interface Interest {
	area: string;
	interest: {
		id?: string;
		label: string;
	};
	weight: number;
}

interface Body {
	id: string;
	interests: Array<Interest>;
	navigator: {
		userAgent: string;
		language: string;
	};
}

type UseSendInterestsReturn = [
	Omit<UseFetchStateProps<unknown>, "data">,
	(setQuery: { interests: Array<Interest> }) => void,
];

/**
 * Hook to send GPX interests to Griddo's Public API.
 *
 * @example
 * const [{ isLoading }, setQuery] = useSendInterests();
 *
 * // Send interests
 * setQuery({
 *   interests: [{ area: "Area1", interest: {label: "Interest1", id: 1}, weight: 1 }]
 * });
 *
 */
export function useSendInterests(): UseSendInterestsReturn {
	const { publicApiUrl } = useSite();
	// init with null to prevent call useFetch without a valid body.
	const [body, setBody] = React.useState<Body | null>(null);

	const [id] = React.useState(() => {
		if (typeof window === "undefined") return "";
		try {
			let storedId = localStorage.getItem("gpx_id");
			if (!storedId) {
				storedId = uuidv4();
				localStorage.setItem("gpx_id", storedId);
			}
			return storedId;
		} catch (error) {
			console.error("Error accessing localStorage:", error);
			return uuidv4();
		}
	});

	const setQuery = (options: { interests: Array<Interest> }) => {
		try {
			const { interests } = options;

			if (!Array.isArray(interests) || interests.length === 0) {
				throw new Error("Interests must be a non-empty array");
			}

			setBody({
				id,
				interests,
				navigator: {
					userAgent: window.navigator.userAgent,
					language: window.navigator.language,
				},
			});
		} catch (error) {
			console.error("Error preparing GPX interests:", error);
		}
	};

	const { isError, isFirstFetch, isLoading, msg } = useFetch(
		`${publicApiUrl}/gpx/collect`,
		{
			method: "POST",
			body,
		},
	);

	return [
		{
			isError,
			isFirstFetch,
			isLoading,
			msg,
		},
		setQuery,
	];
}
